import { Component, ReactNode } from 'react';
import { getFaro } from '../observability/faro';
import {
  canReloadForStaleChunk,
  isReloadInFlight,
  isStaleChunkError,
  reloadForStaleChunk,
} from '../utils/staleChunk';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  reloading: boolean;
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
  }

  /** @public React invokes this static lifecycle method by name when a child throws. */
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Render nothing (not the error screen) while componentDidCatch reloads
    // the page for a stale post-deploy chunk, and for any follow-up error
    // thrown while that reload is tearing the document down.
    return {
      hasError: true,
      error,
      reloading: isReloadInFlight() || (isStaleChunkError(error) && canReloadForStaleChunk()),
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // The page is already tearing down for an auto-reload; don't report the
    // follow-up failure or flash the error screen.
    if (isReloadInFlight()) return;
    // A dynamic import that 404s right after a deploy means this tab is
    // running the previous build; reload once to pick up the new one.
    // The guard in reloadForStaleChunk keeps repeat failures on the normal
    // error path below.
    if (isStaleChunkError(error) && reloadForStaleChunk()) return;
    this.setState({ reloading: false });
    console.error('ErrorBoundary caught:', error, errorInfo);
    // React swallows errors caught here (they never reach window.onerror), so
    // report them to Faro explicitly. No-op when observability is disabled.
    getFaro()?.api.pushError(error, {
      context: { componentStack: errorInfo.componentStack ?? '' },
    });
  }

  render() {
    if (this.state.reloading) {
      return null;
    }

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            minHeight: '40vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            gap: '16px',
            padding: '48px 24px',
            fontFamily: 'var(--font-main, Helvetica Neue, sans-serif)',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: '0.5px',
              color: 'var(--fg-primary, #1a1917)',
            }}
          >
            Something went wrong
          </div>
          <p
            style={{
              maxWidth: 420,
              color: 'var(--fg-secondary, #3d3a34)',
              fontSize: '13px',
              lineHeight: 1.5,
            }}
          >
            An unexpected error occurred. Please reload the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: 'var(--fg-primary, #1a1917)',
              color: 'var(--bg-primary, #f5f0e6)',
              border: 'none',
              padding: '8px 16px',
              fontSize: '11px',
              textTransform: 'uppercase',
              fontWeight: 600,
              letterSpacing: '0.5px',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
